import React, { useState } from "react";
import { ScanLine, CheckCircle2, XCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function AppAuthenticity() {
  const [cert, setCert] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [owner, setOwner] = useState({ owner_name: "", owner_email: "" });

  const verify = async (id) => {
    setBusy(true);
    try {
      const r = await api.get(`/authenticity/${id}`);
      setResult({ ok: true, ...r.data });
    } catch {
      setResult({ ok: false });
    } finally {
      setBusy(false);
    }
  };

  const register = async () => {
    try {
      await api.post("/authenticity/register", {
        certificate_id: result.certificate.certificate_id,
        owner_name: owner.owner_name,
        owner_email: owner.owner_email,
      });
      toast.success("Warranty registered. Welcome to the house.");
      verify(result.certificate.certificate_id);
    } catch {
      toast.error("Could not register");
    }
  };

  return (
    <div data-testid="app-authenticity" className="pb-6">
      <header className="px-5 pt-12">
        <div className="text-[11px] tracking-luxury uppercase text-poche-gold">Authenticity</div>
        <h1 className="font-serif text-2xl mt-1">Verify your piece.</h1>
      </header>

      <div className="mx-5 mt-4">
        <div className="bg-poche-ink border border-poche-gold/30 aspect-square relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-6 border border-poche-gold/40">
            <div className="absolute inset-0 shimmer-gold" />
          </div>
          <div className="text-center px-6 z-10">
            <ScanLine size={32} className="text-poche-gold mx-auto" strokeWidth={1.4} />
            <p className="text-xs text-white/55 mt-3">Camera placeholder · Type the certificate ID printed inside your case to verify.</p>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <input
            data-testid="cert-input"
            value={cert}
            onChange={(e) => setCert(e.target.value)}
            placeholder="JP-LEGACY-2026-001"
            className="flex-1 bg-transparent border border-white/15 px-3 py-3 text-sm focus:border-poche-gold focus:outline-none"
          />
          <button data-testid="verify-btn" disabled={!cert || busy} onClick={() => verify(cert)} className="bg-poche-gold text-poche-black text-[10px] tracking-luxury uppercase px-4 disabled:opacity-50">
            {busy ? "..." : "Verify"}
          </button>
        </div>
        <div className="text-[10px] tracking-luxury uppercase text-white/35 mt-2">Try: JP-LEGACY-2026-001 or POCHE-2026-CAP-001</div>
      </div>

      {result && (
        <div className="mx-5 mt-6">
          {result.ok ? (
            <div className="bg-poche-gold/10 border border-poche-gold/40 p-5" data-testid="cert-valid">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={22} className="text-poche-gold" />
                <div className="font-serif text-xl text-poche-gold">Authentic Joseph Poche</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Info t="Certificate" v={result.certificate.certificate_id} />
                <Info t="Serial" v={result.certificate.serial_number} />
                <Info t="Product" v={result.product?.subtitle} />
                <Info t="Warranty" v={result.certificate.warranty_status} />
              </div>
              {result.certificate.warranty_status === "unregistered" && (
                <div className="mt-5 border-t border-poche-gold/20 pt-4 space-y-2">
                  <div className="text-[10px] tracking-luxury uppercase text-white/55">Register Warranty</div>
                  <input data-testid="reg-owner-name" placeholder="Your name" value={owner.owner_name} onChange={(e) => setOwner({ ...owner, owner_name: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-poche-gold focus:outline-none" />
                  <input data-testid="reg-owner-email" placeholder="Your email" value={owner.owner_email} onChange={(e) => setOwner({ ...owner, owner_email: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-poche-gold focus:outline-none" />
                  <button data-testid="reg-warranty" disabled={!owner.owner_name || !owner.owner_email} onClick={register} className="btn-gold w-full justify-center !py-3 disabled:opacity-50 text-[11px]">Register</button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-red-950/20 border border-red-700/40 p-5 text-center" data-testid="cert-invalid">
              <XCircle size={22} className="text-red-400 mx-auto" />
              <div className="font-serif text-xl text-red-300 mt-2">Not Found</div>
              <p className="text-xs text-white/55 mt-2">This certificate does not match our records. If you believe this piece is genuine, contact concierge@josephpoche.com.</p>
            </div>
          )}
        </div>
      )}

      <div className="px-5 mt-6 flex items-center gap-3 text-[10px] tracking-luxury uppercase text-white/45">
        <Shield size={14} className="text-poche-gold" />
        Every piece ships with a unique QR certificate.
      </div>
    </div>
  );
}

function Info({ t, v }) {
  return (
    <div>
      <div className="text-[9px] tracking-luxury uppercase text-white/45">{t}</div>
      <div className="text-white">{v || "—"}</div>
    </div>
  );
}
